import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHeart, FaRegHeart, FaTrash, FaCode, FaCheck, FaRecycle, FaGlobe, FaLock, FaSave, FaClipboard, FaDownload, FaCrown } from 'react-icons/fa';
import RetroLoadingDots from './RetroLoadingDots';
import ConfirmDialog from './ConfirmDialog';
import NumberSelectionDialog from './NumberSelectionDialog';
import { memo } from 'react';
import RetroFreeDrawing from './RetroFreeDrawing';
import { getEmojisFromActiveSpices, getSpiceIdsFromPrompt, getBasePromptOnly } from './SpiceRack';


/**
 * @ai-component ImageCard
 * @ai-description Main card component for displaying images with hover controls and status indicators
 * 
 * @ai-connection NumberSelectionDialog
 * - Used for regeneration flow to select number of images (1-4)
 * - Triggered by top-left regenerate button
 * - Connects to parent's onRegenerate callback
 * 
 * @ai-connection ConfirmDialog
 * - Used for delete confirmation ("delete" type)
 * - Used for copy confirmation ("copy" type)
 * - Each type has specific styling and behavior
 * 
 * @ai-state-management
 * - Background cards appear on hover OR when highlighted by filter controls
 * - Color logic: green for public, red for liked (priority to public when both)
 * - Corner controls only show on direct card hover
 */

// Import colors from RetroWordmark
const colors = [
  '#00FF9F', // UI Green
  '#FF1493', // Deep pink
  '#0066FF', // Bright blue
  '#FF6B00', // Orange
  '#FF00FF', // Hot pink
  '#FFB800', // Amber
  '#FF4D00', // Bright orange
  '#FF8A00'  // Golden orange
];

const ImageCard = memo(({
  image,
  isFavorited,
  isNew,
  onToggleFavorite,
  onDelete,
  onClick,
  layoutId,
  isSelected,
  onFavorite,
  onImageLoaded,
  onRegenerate,
  resetRotationKey,
  isSourceForGeneration,
  isGroupedView,
  showFavoriteCount,
  isHighlightingLikes,
  isHighlightingPublic,
  onTogglePublic,
  onCopyToPrompt,
  renderOverlay,
  forceShowLikes,
  gameResults,
  showGameResults,
  onDismissResults,
  onSaveDrawing,
  isGenerating,
  hideHoverButtons = false,
  disableBackgroundEffects = false,
  hasBlackBackground = false,
  isAnimated = true,
  isAdminView = false
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFullSizeLoaded, setIsFullSizeLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);
  const [showNumberSelection, setShowNumberSelection] = useState(false);
  const [showUnlikeConfirm, setShowUnlikeConfirm] = useState(false);
  const [localFavorited, setLocalFavorited] = useState(isFavorited);
  const [localCount, setLocalCount] = useState(image.favoriteCount || 0);
  const [showUnlikeAlert, setShowUnlikeAlert] = useState(false);
  const [shouldRemove, setShouldRemove] = useState(false);
  const [isNewLocal, setIsNewLocal] = useState(isNew);
  const [showDismissButton, setShowDismissButton] = useState(true);
  const imageRef = useRef(null);
  const fullSizeImageRef = useRef(null);
  const [loadError, setLoadError] = useState(false);
  const [hoverColor, setHoverColor] = useState(colors[0]);
  const [showFloatingHeart, setShowFloatingHeart] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDownloadingDialog, setShowDownloadingDialog] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [errorLogged, setErrorLogged] = useState(false);

  // Random rotations that change on hover
  const [randomRotation] = useState(() => Math.random() * 4 - 2); // -2 to +2 degrees
  const [bgRandomRotation] = useState(() => Math.random() * 4 - 2);
  const [hoverRotation, setHoverRotation] = useState(0);
  const [bgHoverRotation, setBgHoverRotation] = useState(0);
  const [highlightRotation] = useState(() => Math.random() * 6 - 3); // -3 to +3 degrees
  const [bgHighlightRotation] = useState(() => -highlightRotation); // Opposite of highlight rotation

  // Add generating animation state
  const [generatingColorIndex, setGeneratingColorIndex] = useState(0);
  const [generatingRotation, setGeneratingRotation] = useState(0);

  // Generate new random rotations on hover
  useEffect(() => {
    if (isHovered) {
      setHoverRotation(Math.random() * 8 - 4); // -4 to +4 degrees
      setBgHoverRotation(Math.random() * 12 - 6); // -6 to +6 degrees
    }
  }, [isHovered]);

  // Effect to animate generating state
  useEffect(() => {
    if (!isGenerating) return;
    
    const colorInterval = setInterval(() => {
      setGeneratingColorIndex(prev => (prev + 1) % colors.length);
    }, 2000);

    const rotationInterval = setInterval(() => {
      setGeneratingRotation(prev => prev + 360);
    }, 8000);

    return () => {
      clearInterval(colorInterval);
      clearInterval(rotationInterval);
    };
  }, [isGenerating]);

  // Memoize image URLs
  const { thumbnailUrl, fullSizeUrl } = useMemo(() => {
    // Don't process URLs for placeholder images during generation
    if (image.id.startsWith('placeholder-')) {
      return { thumbnailUrl: null, fullSizeUrl: null };
    }
    
    // Get both URLs upfront
    const fullSize = image.url || image.s3Url;
    const thumbnail = image.thumbnailUrl;
    
    // Validate URLs more strictly
    const isValidUrl = (url) => {
      return url && 
             typeof url === 'string' && 
             url.startsWith('http') && 
             !url.includes('undefined') &&
             !url.includes('null');
    };
    
    const validFullSize = isValidUrl(fullSize) ? fullSize : null;
    const validThumbnail = isValidUrl(thumbnail) ? thumbnail : null;
    
    // Prefer thumbnail, but fallback to fullSize if thumbnail is invalid
    let preferredUrl = validThumbnail || validFullSize;
    let fallbackUrl = validThumbnail ? validFullSize : null;
    
    // Only log if there are serious issues (no valid URLs at all)
    if (!preferredUrl && process.env.NODE_ENV === 'development') {
      console.warn(`[RETRO] ⚠️ No valid URLs for ${image.id}:`, {
        thumbnailProvided: !!thumbnail,
        fullSizeProvided: !!fullSize,
        thumbnailValid: !!validThumbnail,
        fullSizeValid: !!validFullSize,
        timestamp: new Date().toISOString()
      });
    }
    
    return {
      thumbnailUrl: preferredUrl,
      fullSizeUrl: fallbackUrl
    };
  }, [image.thumbnailUrl, image.s3Url, image.url, image.id]);

  // Cleanup image references on unmount
  useEffect(() => {
    return () => {
      if (imageRef.current) {
        imageRef.current.onload = null;
        imageRef.current.onerror = null;
        imageRef.current.src = '';
      }
    };
  }, []);

  const handleImageLoad = () => {
    setIsLoaded(true);
    setLoadError(false);
    onImageLoaded?.(image.id);
  };

  const handleImageError = (e) => {
    setLoadError(true);
    
    // Implement fallback strategy (no logging for normal network issues)
    if (retryCount < 2) {
      setTimeout(() => {
        if (imageRef.current) {
          setRetryCount(prev => prev + 1);
          
          // Try different URL on retry
          if (retryCount === 0 && fullSizeUrl && e.target.src !== fullSizeUrl) {
            // First retry: try full-size URL
            imageRef.current.src = fullSizeUrl;
          } else if (retryCount === 1 && thumbnailUrl && e.target.src !== thumbnailUrl) {
            // Second retry: try thumbnail URL again (in case of network issue)
            imageRef.current.src = thumbnailUrl;
          }
        }
      }, 1000 * (retryCount + 1)); // Exponential backoff
    } else {
      // Only log if all retries failed AND we're in development
      if (process.env.NODE_ENV === 'development' && !errorLogged) {
        setErrorLogged(true);
        console.warn(`[RETRO] Image load failed after retries: ${image.id}`);
      }
    }
  };

  // Simplify highlight logic
  const isHighlighted = useMemo(() => {
    if (disableBackgroundEffects) return false;
    if (!isHovered && !isHighlightingLikes && !isHighlightingPublic) return false;
    if (isHighlightingPublic && image.public) return true;
    if (isHighlightingLikes && localFavorited) return true;
    return isHovered;
  }, [isHovered, isHighlightingLikes, isHighlightingPublic, image.public, localFavorited, disableBackgroundEffects]);

  // Memoize background color calculation
  const bgColor = useMemo(() => {
    if (!isHighlighted) return 'transparent';
    if (isHighlightingPublic && image.public) return 'rgb(74, 222, 128)';
    if ((isHighlightingLikes && localFavorited) || (isHovered && localFavorited)) return '#FF9ECD';
    if (isHovered) return '#FFB800';
    return 'transparent';
  }, [isHighlighted, isHighlightingPublic, image.public, isHighlightingLikes, localFavorited, isHovered]);

  // Handle favorite click with local count update and animation
  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    const newFavorited = !localFavorited;
    setLocalFavorited(newFavorited);
    // Update local count immediately
    setLocalCount(prev => newFavorited ? prev + 1 : Math.max(0, prev - 1));
    
    // Trigger floating heart animation if favoriting
    if (newFavorited) {
      setShowFloatingHeart(true);
      setTimeout(() => setShowFloatingHeart(false), 1000);
    }
    
    onToggleFavorite?.();
  };

  // Update localFavorited when isFavorited prop changes
  useEffect(() => {
    if (localFavorited !== isFavorited) {
      setLocalFavorited(isFavorited);
    }
  }, [isFavorited, localFavorited, image.id]);

  // Sync isNewLocal with isNew
  useEffect(() => {
    setIsNewLocal(isNew);
  }, [isNew]);

  // Track game results state changes in development
  useEffect(() => {
    // Removed debug logging
  }, [showGameResults, gameResults, image.id]);

  // Keep refs for overlay state if needed by other functions
  const prevRenderOverlayRef = useRef(renderOverlay);
  const prevIsLoadedRef = useRef(isLoaded);

  useEffect(() => {
    prevRenderOverlayRef.current = renderOverlay;
    prevIsLoadedRef.current = isLoaded;
  }, [renderOverlay, isLoaded]);

  // Track overlay rendering - cleaned up
  const handleOverlayRender = () => {
    // Removed debug logging
  };

  const handleOverlayRemove = () => {
    // Removed debug logging  
  };

  const handleCopyPrompt = async () => {
    try {

      let basePrompt = '';
      let spiceIds = [];
      let fullPrompt = '';

      // Check if we have the new database fields
      if (image.basePrompt && image.spiceIds) {
        basePrompt = image.basePrompt;
        spiceIds = image.spiceIds;
      } else if (image.prompt) {
        // For legacy images, extract the base prompt and spice IDs from the full prompt
        spiceIds = getSpiceIdsFromPrompt(image.prompt);
        basePrompt = getBasePromptOnly(image.prompt);
      } else {
        basePrompt = '';
        spiceIds = [];
      }
      
      await navigator.clipboard.writeText(basePrompt);
      
      // Create image data with the extracted information
      const imageDataForCallback = {
        ...image,
        basePrompt: basePrompt,
        spiceIds: spiceIds
      };
      
      onCopyToPrompt?.(imageDataForCallback);
      
      // Show copy confirmation
      setShowCopyConfirm(true);
      setTimeout(() => setShowCopyConfirm(false), 3000);

    } catch (err) {
      console.error('❌ [COPY] Failed to copy text:', err);
    }
  };

  const handleRegenerate = () => {
    setShowNumberSelection(true);
  };

  const handleNumberSelected = (numImages) => {
    // Use basePrompt and spiceIds directly, let server build the full prompt
    const basePrompt = image.basePrompt || image.prompt || '';
    const spiceIds = image.spiceIds || [];
    onRegenerate(basePrompt, spiceIds, numImages, image);
    setShowNumberSelection(false);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setIsDeleting(true);
    setTimeout(() => {
      onDelete?.(image.id);
    }, 200);
  };

  // Determine if likes should be shown (only on hover, not when favorited)
  const shouldShowLikes = !disableBackgroundEffects && 
    !image.id.startsWith('placeholder-') && 
    isLoaded && 
    !hideHoverButtons &&
    (isHovered || isHighlightingLikes || forceShowLikes);

  // Determine if favorite background card should be shown (when favorited but not hovered)
  const shouldShowFavoriteCard = !disableBackgroundEffects && 
    !image.id.startsWith('placeholder-') && 
    isLoaded && 
    localFavorited && 
    !isHovered && 
    !isHighlightingLikes &&
    !isHighlightingPublic;
  
  // Determine highlight color
  let highlightColor = '';
  if (isHighlightingLikes && localFavorited) {
    highlightColor = '#FF9ECD';
  } else if (isHighlightingPublic && image.public) {
    highlightColor = 'rgb(74, 222, 128)';
  }

  // Handle dismissing results and showing image
  const handleDismissResults = () => {
    if (process.env.NODE_ENV === 'development') {
  
    }
    setShouldRemove(true);
    setTimeout(() => {
      setIsNewLocal(false);
    }, 300);
  };

  // Memoize the animation variants to prevent recalculation
  const variants = useMemo(() => ({
    highlighted: {
      scale: 1.02,
      rotate: randomRotation,
      zIndex: 1,
      backgroundColor: bgColor,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 25,
        mass: 0.5,
        backgroundColor: {
          type: "tween",
          duration: 0.3
        }
      }
    },
    normal: {
      scale: 0.95,
      rotate: isNew ? randomRotation : 0,
      zIndex: 0,
      backgroundColor: 'transparent',
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 25,
        mass: 0.5,
        backgroundColor: {
          type: "tween",
          duration: 0.3
        }
      }
    },
    hover: {
      scale: 1.01,
      rotate: randomRotation,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 25,
        mass: 0.5
      }
    },
    tap: {
      scale: 0.98,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 25,
        mass: 0.5
      }
    }
  }), [randomRotation, isNew, bgColor]);

  // Effect for hover state changes
  useEffect(() => {
    // Keep the effect for state management, but remove the logging
  }, [isHovered, randomRotation, isHighlighted, image.id]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleTogglePublic = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/images/${image.id}/toggle-public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          isPublic: !image.public
        })
      });

      if (!response.ok) {
        throw new Error('Failed to toggle public status');
      }

      const updatedImage = await response.json();
      onTogglePublic?.(updatedImage);
    } catch (error) {
      console.error('Failed to toggle public status:', error);
    }
  };

  const handleClick = (e) => {
    onClick?.(image);
  };

  const handleDownload = useCallback(async (e) => {
    e.stopPropagation();
    
    try {
      // Show downloading dialog
      setShowDownloadingDialog(true);
      
      // Use full size URL if available, fallback to thumbnail
      const downloadUrl = fullSizeUrl || thumbnailUrl;
      if (!downloadUrl) return;

      // Fetch the image
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      
      // Create object URL
      const url = window.URL.createObjectURL(blob);
      
      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `image-${image.id}.png`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      // Keep dialog showing for 3s
      setTimeout(() => {
        setShowDownloadingDialog(false);
      }, 3000);
      

    } catch (error) {
      console.error('Download error:', error);
      setShowDownloadingDialog(false);
    }
  }, [image.id, fullSizeUrl, thumbnailUrl]);

  // Main image render
  const renderImage = () => (
    <div className={`w-full h-full rounded-xl ${hasBlackBackground ? 'bg-black' : ''}`}>
      <img
        ref={imageRef}
        src={thumbnailUrl || ''}
        alt={image.prompt}
        crossOrigin="anonymous"
        className={`w-full h-full object-contain rounded-xl transition-opacity duration-500 ease-in-out ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        loading="lazy"
      />
    </div>
  );

  // Pick a random color on mount and when hover starts
  useEffect(() => {
    if (isHovered) {
      setHoverColor(colors[Math.floor(Math.random() * colors.length)]);
    }
  }, [isHovered]);

  return (
    <motion.div
      className={`relative group aspect-square hover:z-10`}
      layoutId={layoutId}
      style={{
        margin: '4px', // Add margin to provide rotation space
        overflow: 'visible'
      }}
      animate={{
        scale: isDeleting ? 0 : 1,
        opacity: isDeleting ? 1 : 1
      }}
      whileHover={{ scale: 1.02 }}
      transition={{
        duration: 0.2,
        ease: "easeIn"
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Download dialog */}
      {showDownloadingDialog && (
        <ConfirmDialog
          isOpen={showDownloadingDialog}
          onClose={() => setShowDownloadingDialog(false)}
          type="downloading"
        />
      )}

      {/* Rotating background card - always behind everything */}
      <AnimatePresence>
        {isGenerating && (
          <motion.div
            className="absolute -inset-4 rounded-xl shadow-lg"
            style={{
              background: colors[generatingColorIndex],
              transformOrigin: 'center center',
              zIndex: 0
            }}
            initial={{ 
              rotate: 0,
              scale: 1.1,
              opacity: 0
            }}
            animate={{ 
              rotate: generatingRotation,
              scale: 1.1,
              opacity: 0.7
            }}
            exit={{
              opacity: 0,
              scale: 1.1,
              transition: { duration: 0.2 }
            }}
            transition={{
              rotate: {
                duration: 8,
                ease: "linear",
                repeat: Infinity
              },
              scale: {
                type: "spring",
                stiffness: 300,
                damping: 25,
                mass: 1
              }
            }}
          >

          </motion.div>
        )}
      </AnimatePresence>

      {/* Background cards */}
      <AnimatePresence>
        {/* Favorite background card - subtle rotation to indicate favorited status */}
        {shouldShowFavoriteCard && !isGenerating && (
          <motion.div
            className="absolute inset-0 rounded-xl shadow-lg"
            style={{
              background: '#FF9ECD',
              transformOrigin: 'center center',
              zIndex: 1
            }}
            initial={{ 
              rotate: 0,
              scale: 1.02,
              opacity: 0,
              x: 0,
              y: 0
            }}
            animate={{ 
              rotate: 2,
              scale: 1.02,
              opacity: 0.3,
              x: 3,
              y: -2
            }}
            exit={{
              opacity: 0,
              scale: 1.02,
              rotate: 0,
              x: 0,
              y: 0,
              transition: { duration: 0.2 }
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
              mass: 1
            }}
          />
        )}

        {/* Existing highlight background - only show when not generating */}
        {isHighlighted && !isGenerating && (
          <motion.div
            className="absolute inset-0 rounded-xl shadow-lg"
            style={{
              background: bgColor,
              transformOrigin: 'center center',
              zIndex: 1
            }}
            initial={{ 
              rotate: bgHighlightRotation,
              scale: 1.05,
              opacity: 0
            }}
            animate={{ 
              rotate: isHovered ? -hoverRotation * 0.7 : bgHighlightRotation,
              scale: isHovered ? 1.08 : 1.05,
              opacity: 0.8
            }}
            exit={{
              opacity: 0,
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
              mass: 1
            }}
          />
        )}
      </AnimatePresence>

      {/* Main card */}
      <motion.div 
        className="relative w-full h-full cursor-pointer aspect-square"
        style={{
          position: 'relative',
          zIndex: 2
        }}
        onClick={handleClick}
        animate={{
          scale: isHovered ? 1.02 : 1,
          rotate: isHovered ? hoverRotation : randomRotation,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 25,
          mass: 1
        }}
      >
        {/* Image container with proper stacking */}
        <div className={`w-full h-full rounded-xl aspect-square ${hasBlackBackground ? 'bg-black' : ''}`}>
          <img
            ref={imageRef}
            src={thumbnailUrl || ''}
            alt={image.prompt}
            crossOrigin="anonymous"
            className={`w-full h-full object-contain rounded-xl transition-opacity duration-300 ease-in-out ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading="lazy"
          />
        </div>

        {/* Overlay - only show for placeholder images during generation */}
        {image.id.startsWith('placeholder-') && renderOverlay && (
          <div 
            className="absolute inset-0 rounded-xl overflow-hidden aspect-square"
            style={{ 
              position: 'absolute',
              inset: 0
            }}
          >
            {renderOverlay()}
          </div>
        )}

        {/* Like button */}
        {shouldShowLikes && (
          <motion.button
            className="absolute top-2 left-2 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            style={{
              zIndex: 4
            }}
            onClick={handleFavoriteClick}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
          >
            <div className="flex items-center gap-2">
              {/* Floating heart animation */}
              <AnimatePresence>
                {showFloatingHeart && (
                  <motion.div
                    initial={{ opacity: 1, scale: 1, y: 0 }}
                    animate={{ opacity: 0, scale: 1.5, y: -20 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="absolute left-2 pointer-events-none"
                  >
                    <FaHeart className="w-5 h-5 text-red-500" />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Static heart with bounce animation */}
              <motion.div
                animate={{ 
                  scale: localFavorited ? [1, 1.3, 1] : 1,
                  rotate: localFavorited ? [0, -10, 10, 0] : 0
                }}
                transition={{ duration: 0.4 }}
              >
                {localFavorited ? (
                  <FaHeart className="w-5 h-5 text-red-500" />
                ) : (
                  <FaRegHeart className="w-5 h-5 text-white" />
                )}
              </motion.div>
              {showFavoriteCount && localCount > 0 && (
                <motion.span 
                  key={localCount}
                  initial={{ rotateX: -90 }}
                  animate={{ rotateX: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className={`text-[14px] font-['VT323'] ${localFavorited ? 'text-red-500' : 'text-white'}`}
                >
                  {localCount}
                </motion.span>
              )}
            </div>
          </motion.button>
        )}

        {/* Hover controls - ONLY show when not in a dialog state and not in compact mode */}
        <AnimatePresence>
          {isHovered && !hideHoverButtons && !renderOverlay && !showDownloadingDialog && !showDeleteConfirm && !showCopyConfirm && (
            <>
              {/* Top right - Regenerate and Download */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="absolute top-2 right-2 flex items-center gap-2 z-20"
              >
                {onRegenerate && (
                  <motion.button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowNumberSelection(true);
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-2 bg-black/50 rounded-full text-white hover:text-white/80 transition-colors"
                  >
                    <FaRecycle className="w-4 h-4" />
                  </motion.button>
                )}
                
                <motion.button
                  onClick={handleDownload}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-2 bg-black/50 rounded-full text-white hover:text-white/80 transition-colors"
                >
                  <FaDownload className="w-4 h-4" />
                </motion.button>
              </motion.div>

              {/* Bottom left - Copy prompt */}
              {onCopyToPrompt && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyPrompt();
                  }}
                  className="absolute bottom-2 left-2 p-2 bg-black/50 rounded-full text-white hover:text-white/80 transition-colors z-20"
                >
                  {showCopyConfirm ? (
                    <FaCheck className="w-4 h-4 text-green-400" />
                  ) : (
                    <FaClipboard className="w-4 h-4" />
                  )}
                </motion.button>
              )}

              {/* Bottom right - Delete */}
              {onDelete && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  className="absolute bottom-2 right-2 p-2 bg-black/50 rounded-full text-white hover:text-white/80 transition-colors z-20"
                >
                  <FaTrash className="w-4 h-4" />
                </motion.button>
              )}
            </>
          )}
        </AnimatePresence>

        {/* Confirmation Dialogs */}
        <ConfirmDialog
          isOpen={showDownloadingDialog}
          onClose={() => setShowDownloadingDialog(false)}
          type="downloading"
        />
        {showNumberSelection && (
          <NumberSelectionDialog
            isOpen={showNumberSelection}
            onClose={() => setShowNumberSelection(false)}
            onSelect={handleNumberSelected}
          />
        )}
        {showCopyConfirm && (
          <ConfirmDialog
            isOpen={showCopyConfirm}
            onClose={() => setShowCopyConfirm(false)}
            type="copy"
          />
        )}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
          type="delete"
        />
      </motion.div>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  return (
    prevProps.image.id === nextProps.image.id &&
    prevProps.isFavorited === nextProps.isFavorited &&
    prevProps.isHighlightingLikes === nextProps.isHighlightingLikes &&
    prevProps.isHighlightingPublic === nextProps.isHighlightingPublic &&
    prevProps.forceShowLikes === nextProps.forceShowLikes &&
    prevProps.renderOverlay === nextProps.renderOverlay
  );
});

export default ImageCard; 